import React, { useState } from "react";
import { Form, Modal, Typography } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import PrimaryButton from "../Forms/PrimaryButton";
import FormInput from "../Forms/FormInput";

const { Title } = Typography;

export default function EditOrderModal({
  open,
  onClose,
  handleSubmit,
  productToEdit,
  setProductToEdit,
  loading,
}) {
  const [isError, setIsError] = useState(false);
  const [form] = Form.useForm();
  const [formErrors, setFormErrors] = useState({});

  const validateFields = () => {
    const errors = {};

    const cases = Number(productToEdit?.update_total_cases ?? productToEdit?.cases);
    if (!cases || isNaN(cases) || cases < 1 || !Number.isInteger(cases)) {
      errors.update_total_cases = "Total Cases must be a whole number ≥ 1";
    }

    const meters = Number(productToEdit?.update_total_meter ?? productToEdit?.quantity_meters);
    if (!meters || isNaN(meters) || meters < 1) {
      errors.update_total_meter = "Total Meters must be a number ≥ 1";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onFormSubmit = () => {
    if (validateFields()) {
      handleSubmit(productToEdit);
    }
  };

  const handleOnBlur = () => {
    validateFields();
  };

  // Helper function to block "e", "E", "+", and "-" keys
  const blockInvalidNumberChars = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <Modal
      className="form-css-2 custom-modal"
      open={open}
      footer={null}
      onCancel={() => {
        setFormErrors({});
        onClose();
      }}
      centered
    >
      {/* Header */}
      <div className="flex justify-between items-center">
        <Title level={2} className="text-xl font-semibold text-gray-800 font-abhaya m-0 ">
          Edit Order Details
        </Title>
      </div>

      {/* Product Info */}
      <div className="bg-white p-4 rounded-lg mb-4 shadow-sm flex items-center mx-8 border border-gray-200 mt-4">
        <div className="w-16 h-16 rounded-lg mr-4 overflow-hidden flex items-center justify-center">
          {productToEdit?.product_picture_url && !isError ? (
            <img
              src={productToEdit.product_picture_url}
              alt={productToEdit?.short_name}
              className="w-full h-full object-cover"
              onError={() => setIsError(true)}
            />
          ) : (
            <ExclamationCircleFilled className="text-gray-700 text-2xl" />
          )}
        </div>
        <div>
          <div className="font-semibold text-lg">{productToEdit?.short_name}</div>
          <div className="text-sm text-gray-500">{productToEdit?.shade_name}</div>
        </div>
      </div>

      <div className="">
        <Form
          layout="vertical"
          onFinish={onFormSubmit}
          initialValues={productToEdit}
          form={form}
          noValidate
        >
          <FormInput
            label="Total Cases"
            type="number"
            className="no-spinner rounded-lg h-10 w-full px-3"
            style={{ marginBottom: 0 }}
            value={productToEdit?.update_total_cases ?? productToEdit?.cases}
            onChange={(e) =>
              setProductToEdit({ ...productToEdit, update_total_cases: e.target.value })
            }
            onKeyDown={blockInvalidNumberChars}
            placeholder="Enter total cases"
            onBlur={handleOnBlur}
            onWheel={(e) => {
              e.preventDefault();
            }}
          />
          {formErrors.update_total_cases && (
            <div className="text-red-500 text-sm mb-2">{formErrors.update_total_cases}</div>
          )}

          <FormInput
            label="Total Meters"
            type="number"
            className="no-spinner rounded-lg h-10 w-full px-3"
            style={{ marginBottom: 0, marginTop: "10px" }}
            value={productToEdit?.update_total_meter ?? productToEdit?.quantity_meters}
            onChange={(e) =>
              setProductToEdit({ ...productToEdit, update_total_meter: e.target.value })
            }
            onKeyDown={blockInvalidNumberChars}
            placeholder="Enter total meters"
            onBlur={handleOnBlur}
            onWheel={(e) => {
              e.preventDefault();
            }}
          />
          {formErrors.update_total_meter && (
            <div className="text-red-500 text-sm mb-2">{formErrors.update_total_meter}</div>
          )}

          {/* Footer */}
          <div className="w-full mt-4 flex justify-center">
            <PrimaryButton
              className="me-[30px]"
              text="Save and Proceed"
              loading={loading}
              type="submit"
            />
          </div>
        </Form>
      </div>
    </Modal>
  );
}
